import { produce } from 'immer';
import { deepClone, getGuid } from '../helpers/utility';
import { getFormattedMetaColumnName, defaultColumnName } from './helpers';
import {
  REPORTVIEW_DESIGNER_CLEAR,
  REPORTVIEW_DESIGNER_CREATE_INIT,
  REPORTVIEW_DESIGNER_EDIT_INIT,
  REPORTVIEW_DESIGNER_EDIT_INIT_FAILURE,
  REPORTVIEW_DESIGNER_UPDATECOLUMNFIELD,
  REPORTVIEW_DESIGNER_ADDCOLUMN,
  REPORTVIEW_DESIGNER_REMOVECOLUMN,
  REPORTVIEW_DESIGNER_COLUMNMOVELEFT,
  REPORTVIEW_DESIGNER_COLUMNMOVERIGHT,
  REPORTVIEW_DESIGNER_UPDATECOLUMNSORTORDER,
  REPORTVIEW_DESIGNER_UPDATECOLUMNNAME,
  REPORTVIEW_DESIGNER_UPDATEMETACOLUMNNAME,
  REPORTVIEW_DESIGNER_ADDTEMPLATE,
  REPORTVIEW_DESIGNER_REMOVETEMPLATE,
  REPORTVIEW_DESIGNER_CHANGEFILTERS,
  REPORTVIEW_DESIGNER_SETSORT,
  REPORTVIEW_DESIGNER_SAVE_REQUEST,
  REPORTVIEW_DESIGNER_SAVE_SUCCESS,
  REPORTVIEW_DESIGNER_UPDATENAME,
  REPORTVIEW_DESIGNER_REORDERCOLUMSALPHABETICALLY
} from './actions';
import { ReportColumn, ReportDesignerAction, ReportDesignerPayload, ReportDesignerState } from './reports-types';

const initialState: ReportDesignerState = {
  reportView: {
    name: '',
    templates: [],
    columns: []
  }
};

export default function reports(existingState: ReportDesignerState = {}, action: ReportDesignerAction) {
  if (!action) {
    return existingState;
  }

  // `state` can be manipulated per immerjs
  // `existingState` is the base state that can't be modified
  // Change `state` directly or return a new state object
  return produce(existingState, (state: ReportDesignerState) => {
    const { type, payload } = action;
    const { reportView } = state;

    switch (type) {
      case REPORTVIEW_DESIGNER_CLEAR:
        return {};

      case REPORTVIEW_DESIGNER_CREATE_INIT:
        return { ...payload, reportView: { ...initialState.reportView, ...payload?.reportView }, isUnsaved: true, isDirty: false };

      case REPORTVIEW_DESIGNER_EDIT_INIT:
      case REPORTVIEW_DESIGNER_EDIT_INIT_FAILURE:
        return payload;

      case REPORTVIEW_DESIGNER_UPDATECOLUMNFIELD:
        return updateTemplateColumnField(state, payload);

      case REPORTVIEW_DESIGNER_ADDCOLUMN:
        return addColumn(state);

      case REPORTVIEW_DESIGNER_REMOVECOLUMN:
        return removeColumn(state, payload);

      case REPORTVIEW_DESIGNER_COLUMNMOVELEFT:
        return moveColumnLeft(state, payload);

      case REPORTVIEW_DESIGNER_COLUMNMOVERIGHT:
        return moveColumnRight(state, payload);

      case REPORTVIEW_DESIGNER_UPDATECOLUMNSORTORDER:
        return updateColumnSortOrder(state, payload);

      case REPORTVIEW_DESIGNER_UPDATECOLUMNNAME:
        return updateColumnName(state, payload);

      case REPORTVIEW_DESIGNER_UPDATEMETACOLUMNNAME:
        return updateMetaColumnName(state, payload);

      case REPORTVIEW_DESIGNER_ADDTEMPLATE:
        return addTemplate(state, payload);

      case REPORTVIEW_DESIGNER_REMOVETEMPLATE:
        return removeTemplate(state, payload);

      case REPORTVIEW_DESIGNER_CHANGEFILTERS:
        reportView.filters = payload.filters;
        return state;

      case REPORTVIEW_DESIGNER_SETSORT:
        reportView.sort = payload.sort;
        return state;

      case REPORTVIEW_DESIGNER_SAVE_REQUEST:
        state.saving = true;
        return state;

      case REPORTVIEW_DESIGNER_REORDERCOLUMSALPHABETICALLY:
        reportView.columns = deepClone(existingState.reportView.columns).sort((a, b) => a.name.localeCompare(b.name));
        state.isDirty = true;
        state.isUnsaved = true;

        return state;

      case REPORTVIEW_DESIGNER_SAVE_SUCCESS:
        reportView.id = payload.reportViewId;
        state.saving = false;
        state.isUnsaved = false;
        state.isDirty = false;
        state.defaultFilterOpened = false;

        return state;

      case REPORTVIEW_DESIGNER_UPDATENAME:
        reportView.name = payload.name;
        state.isDirty = true;
        state.isUnsaved = true;

        return state;

      default:
        return state;
    }
  });
}

const setUnsaved = (state: ReportDesignerState): ReportDesignerState => {
  state.isUnsaved = true;

  return state;
};

const updateTemplateColumnField = (
  state: ReportDesignerState,
  { templateId, columnId, fieldId, fieldName, fieldType, fieldColumnIndex, isTableRowNumber }: ReportDesignerPayload
) => {
  const { reportView } = state;
  const viewTemplates = reportView.templates;
  const templateIndex = viewTemplates.findIndex((x) => x.templateId === templateId);
  const { columnMap } = reportView.templates[templateIndex];
  let templateColumnIndex = columnMap.findIndex((x) => x.columnId === columnId);

  if (templateColumnIndex === -1) {
    templateColumnIndex = columnMap.length;
  }

  const columnIndex = reportView.columns.findIndex((x) => x.columnId === columnId);

  if (reportView.columns[columnIndex].name === defaultColumnName) {
    reportView.columns[columnIndex].name = fieldName;
  }

  reportView.templates[templateIndex].columnMap[templateColumnIndex] = {
    fieldId,
    columnId,
    fieldType,
    fieldColumnIndex,
    isTableRowNumber
  };

  state.isDirty = true;

  return setUnsaved(state);
};

const addColumn = (state: ReportDesignerState): ReportDesignerState => {
  const { reportView } = state;

  reportView.columns.push({ columnId: getGuid(), name: defaultColumnName, sortOrder: reportView.columns.length });
  reportView.columns = reorderColumns(reportView.columns);

  state.isDirty = true;

  return setUnsaved(state);
};

const removeColumn = (state: ReportDesignerState, { columnId }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;

  reportView.columns = reportView.columns.filter((item) => item.columnId !== columnId);
  reportView.columns = reorderColumns(reportView.columns);
  reportView.templates.forEach((template) => {
    template.columnMap = template.columnMap.filter((t) => t.columnId !== columnId);
  });

  state.isDirty = true;

  return setUnsaved(state);
};

const updateColumnSortOrder = (state: ReportDesignerState, { columnId, sortOrder }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;

  reportView.columns.find((c) => c.columnId === columnId).sortOrder = sortOrder;
  reportView.columns = reorderColumns(reportView.columns);

  state.isDirty = true;

  return setUnsaved(state);
};

const moveColumnLeft = (state: ReportDesignerState, { columnId }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;

  reportView.columns.find((c) => c.columnId === columnId).sortOrder -= 1.1;
  reportView.columns = reorderColumns(reportView.columns);

  state.isDirty = true;

  return setUnsaved(state);
};

const moveColumnRight = (state: ReportDesignerState, { columnId }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;

  reportView.columns.find((c) => c.columnId === columnId).sortOrder += 1.1;
  reportView.columns = reorderColumns(reportView.columns);

  state.isDirty = true;

  return setUnsaved(state);
};

const updateColumnName = (state: ReportDesignerState, { columnId, name }: ReportDesignerPayload): ReportDesignerState => {
  if (!name) {
    return state;
  }

  const { reportView } = state;

  reportView.columns.find((c) => c.columnId === columnId).name = name;

  state.isDirty = true;

  return setUnsaved(state);
};

const updateMetaColumnName = (state: ReportDesignerState, { columnId, metaColumnName }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;
  const column = reportView.columns.find((c) => c.columnId === columnId);

  column.metaColumnName = metaColumnName;
  const name = getFormattedMetaColumnName(metaColumnName);
  column.name = name;

  if (!column.metaColumnName) {
    reportView.templates.forEach((template) => {
      template.columnMap = template.columnMap.filter((t) => t.columnId !== columnId);
    });
  }

  state.isDirty = true;

  return setUnsaved(state);
};

const addTemplate = (state: ReportDesignerState, { templateId }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;

  reportView.templates.push({ templateId, columnMap: [] });

  state.isDirty = true;

  return setUnsaved(state);
};

const removeTemplate = (state: ReportDesignerState, { templateId }: ReportDesignerPayload): ReportDesignerState => {
  const { reportView } = state;

  reportView.templates = reportView.templates.filter((t) => t.templateId !== templateId);

  state.isDirty = true;

  return setUnsaved(state);
};

function reorderColumns(columns: ReportColumn[], property = 'sortOrder', startSortOrder = 0): ReportColumn[] {
  let copy = [...columns];
  let newSortOrderStart = startSortOrder || 0;

  copy.sort((a, b) => a[property] - b[property]);
  // set sort order back to whole numbers
  copy = copy.map((item) => ({ ...item, sortOrder: newSortOrderStart++ }));

  return copy;
}
